/**
 * Schema.org Markup Generators
 *
 * Utility functions to generate schema.org JSON-LD markup for SEO and rich results.
 * All schemas follow schema.org specifications and are validated for Google Rich Results.
 *
 * @module schemaGenerators
 */

import type {
  OrganizationSchema,
  ArticleSchema,
  BreadcrumbListSchema,
  WebPageSchema,
  FAQPageSchema,
} from '../types/schema';
import type { BreadcrumbItem } from '../types/seo';

/**
 * Generate Organization schema for the Robert Garza campaign
 * Used on homepage to establish brand identity and social profiles
 *
 * @returns OrganizationSchema JSON-LD
 *
 * @example
 * ```tsx
 * const schema = generateOrganizationSchema();
 * <Helmet>
 *   <script type="application/ld+json">
 *     {JSON.stringify(schema)}
 *   </script>
 * </Helmet>
 * ```
 */
export function generateOrganizationSchema(): OrganizationSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'RobertGarza.us Family Court Reform',
    url: 'https://robertgarza.us',
    logo: 'https://robertgarza.us/images/robert-garza-logo.png',
    sameAs: [
      // TODO: Replace with actual social media URLs when available (T080)
      // These should be gathered during implementation and updated here
      'https://facebook.com/robertgarzatx',
      'https://twitter.com/robertgarzatx',
      'https://linkedin.com/in/robertgarzatx',
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: 'info@robertgarza.us',
        contactType: 'General Inquiries',
        areaServed: 'US',
      },
    ],
  };
}

/**
 * Generate Article schema for bill detail pages
 * Enables rich results for legislative bill content
 *
 * @param billData - Bill information from Google Sheets
 * @param billData.seoTitle - Bill title for headline (from Google Sheets)
 * @param billData.seoDescription - Bill description (from Google Sheets)
 * @param billData.imageUrl - Optional bill featured image URL
 * @param billData.datePublished - Optional publication date (ISO 8601 format)
 * @param stateName - State name for the bill (e.g., "Texas")
 *
 * @returns ArticleSchema JSON-LD
 *
 * @example
 * ```tsx
 * const schema = generateArticleSchema({
 *   seoTitle: "Three Strikes Law for Judges",
 *   seoDescription: "Remove judges after three due process violations",
 *   imageUrl: "https://robertgarza.us/images/three-strikes-bill.jpg",
 *   datePublished: "2024-03-15T00:00:00Z"
 * }, "Texas");
 * ```
 */
export function generateArticleSchema(
  billData: {
    seoTitle: string;
    seoDescription: string;
    imageUrl?: string;
    datePublished?: string;
    pageUrl?: string;
  },
  stateName?: string,
): ArticleSchema {
  const headline = stateName ? `${billData.seoTitle} - ${stateName}` : billData.seoTitle;
  const pageUrl = billData.pageUrl
    ? billData.pageUrl.startsWith('http')
      ? billData.pageUrl
      : `https://robertgarza.us${billData.pageUrl}`
    : 'https://robertgarza.us';
  const imageUrl = billData.imageUrl
    ? billData.imageUrl.startsWith('http')
      ? billData.imageUrl
      : `https://robertgarza.us${billData.imageUrl}`
    : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description: billData.seoDescription,
    image: imageUrl,
    author: {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Robert Garza',
      url: 'https://robertgarza.us/about-robert-garza',
    },
    publisher: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'RobertGarza.us Family Court Reform',
      url: 'https://robertgarza.us',
      logo: 'https://robertgarza.us/images/robert-garza-logo.png',
    },
    datePublished: billData.datePublished || new Date().toISOString(),
    dateModified: new Date().toISOString(),
    mainEntityOfPage: pageUrl,
  };
}

/**
 * Generate BreadcrumbList schema from breadcrumb items
 * Enables breadcrumb rich results in search
 *
 * @param breadcrumbs - Array of breadcrumb items with name, url, and position
 *
 * @returns BreadcrumbListSchema JSON-LD
 *
 * @example
 * ```tsx
 * const breadcrumbs: BreadcrumbItem[] = [
 *   { name: 'Home', url: '/', position: 1 },
 *   { name: 'Bills', url: '/family-court-reform-bills', position: 2 },
 *   { name: 'Texas', url: '/bill/texas', position: 3 },
 *   { name: 'Three Strikes Law', url: '/bill/texas/three-strikes', position: 4 }
 * ];
 * const schema = generateBreadcrumbSchema(breadcrumbs);
 * ```
 */
export function generateBreadcrumbSchema(breadcrumbs: BreadcrumbItem[]): BreadcrumbListSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs
      .filter((crumb) => crumb.url)
      .map((crumb) => ({
        '@type': 'ListItem' as const,
        position: crumb.position,
        name: crumb.name,
        item: crumb.url!.startsWith('http') ? crumb.url! : `https://robertgarza.us${crumb.url}`,
      })),
  };
}

/**
 * Generate WebPage schema for static pages
 * Provides basic page information for search engines
 *
 * @param pageData - Page information
 * @param pageData.name - Page title
 * @param pageData.description - Page description
 * @param pageData.url - Canonical page URL (absolute or relative)
 *
 * @returns WebPageSchema JSON-LD
 *
 * @example
 * ```tsx
 * const schema = generateWebPageSchema({
 *   name: 'About Robert Garza',
 *   description: 'Learn about Robert Garza and his mission for family justice reform',
 *   url: '/about'
 * });
 * ```
 */
export function generateWebPageSchema(pageData: { name: string; description: string; url: string }): WebPageSchema {
  const absoluteUrl = pageData.url.startsWith('http') ? pageData.url : `https://robertgarza.us${pageData.url}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageData.name,
    description: pageData.description,
    url: absoluteUrl,
  };
}

/**
 * Helper function to combine multiple schemas into an array for Helmet
 * Useful when a page needs multiple schema types (e.g., WebPage + BreadcrumbList)
 *
 * @param schemas - Array of schema objects
 * @returns Array of schemas ready for JSON-LD injection
 *
 * @example
 * ```tsx
 * const schemas = combineSchemas([
 *   generateWebPageSchema({ name: 'About', description: '...', url: '/about' }),
 *   generateOrganizationSchema()
 * ]);
 *
 * <SEO
 *   title="About"
 *   description="..."
 *   path="/about"
 *   schema={schemas}
 * />
 * ```
 */
export function combineSchemas(
  schemas: (OrganizationSchema | ArticleSchema | BreadcrumbListSchema | WebPageSchema | FAQPageSchema)[],
): (OrganizationSchema | ArticleSchema | BreadcrumbListSchema | WebPageSchema | FAQPageSchema)[] {
  return schemas;
}

/**
 * Input type for FAQ items when generating FAQPage schema
 */
export interface FAQInput {
  /** The question text */
  question: string;
  /** The answer text (plain text, no HTML/JSX) */
  answer: string;
}

/**
 * Generate FAQPage schema for FAQ pages
 * Enables FAQ rich results in Google Search
 *
 * @param faqs - Array of question/answer pairs (plain text only)
 *
 * @returns FAQPageSchema JSON-LD
 *
 * @example
 * ```tsx
 * const faqs = [
 *   { question: "What is family court reform?", answer: "Family court reform aims to..." },
 *   { question: "How can I get involved?", answer: "You can get involved by..." }
 * ];
 * const schema = generateFAQPageSchema(faqs);
 * ```
 */
export function generateFAQPageSchema(faqs: FAQInput[]): FAQPageSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs
      .filter((faq) => faq.question && faq.answer) // Skip items without both Q&A
      .map((faq) => ({
        '@type': 'Question' as const,
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer' as const,
          text: faq.answer,
        },
      })),
  };
}
